import * as React from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import { useLocale } from 'hooks'

dayjs.extend(advancedFormat)
dayjs.extend(timezone)

const fetcher = (url: string) => fetch(url).then((res) => res.json())

export const useEstimatedDeliveryDate = () => {
  const { locale } = useLocale()
  const { data, error } = useSWR<EstimatedDeliveryData>(
    '/api/shipping/estimated_delivery_date',
    fetcher,
    {
      revalidateOnFocus: false
    }
  )

  const [isBeforeDateTomorrow, setIsBeforeDateTomorrow] =
    React.useState<boolean>(false)
  const [beforeCutoffDate, setBeforeCutoffDate] = React.useState<string>('')
  const [afterCutoffDate, setAfterCutoffDate] = React.useState<string>('')
  const [isBeforeCutoffTime, setIsBeforeCutoffTime] =
    React.useState<boolean>(true)
  const [remainingTime, setRemainingTime] = React.useState<RemainingTime>(null)

  React.useEffect(() => {
    if (data) {
      const updateRemainingTime = () => {
        // Ensure cutoff time is in BST
        const cutoffTime = dayjs(data.cutoff_time).tz('Europe/London')
        const now = dayjs().tz('Europe/London')
        const diffMilliseconds = cutoffTime.diff(now)
        const hours = Math.floor(diffMilliseconds / (60 * 60 * 1000))
        const minutes = Math.floor(
          (diffMilliseconds % (60 * 60 * 1000)) / (60 * 1000)
        )

        setBeforeCutoffDate(
          dayjs(data.before_cutoff_delivery_date)
            .locale(locale)
            .format('ddd Do MMM')
        )
        setAfterCutoffDate(
          dayjs(data.after_cutoff_delivery_date)
            .locale(locale)
            .format('ddd Do MMM')
        )

        const tomorrow = dayjs().tz('Europe/London').add(1, 'day')

        setIsBeforeDateTomorrow(
          dayjs(data.before_cutoff_delivery_date).isSame(tomorrow, 'day')
        )

        // Cutoff time has less than 1 minute remaining or is not on the current day
        if (diffMilliseconds <= 60000 || !cutoffTime.isSame(now, 'day')) {
          setIsBeforeCutoffTime(false)
        } else {
          setIsBeforeCutoffTime(true)
        }

        setRemainingTime({ hours, minutes })
      }

      updateRemainingTime()

      // Schedule an update every 15 seconds (No reason for every second as we only show minutes)
      const interval = setInterval(updateRemainingTime, 15 * 1000)

      return () => clearInterval(interval)
    }
  }, [data, locale])

  return {
    data,
    error,
    remainingTime,
    afterCutoffDate,
    beforeCutoffDate,
    isBeforeCutoffTime,
    isBeforeDateTomorrow
  }
}

export type RemainingTime = {
  hours: number
  minutes: number
} | null

interface EstimatedDeliveryData {
  cutoff_time: string
  before_cutoff_delivery_date: string
  after_cutoff_delivery_date: string
}
